import {
  type Accessor,
  createContext,
  type ParentComponent,
  useContext,
} from "solid-js";
import { useCompanyStore } from "@/domains/company/company/store/company-store";
import { useBillingStatus, useSubscription } from "../api";
import type { BillingStatusContract, Subscription } from "../api/types";

type BillingContextValue = {
  subscription: Accessor<Subscription | null | undefined>;
  billingStatusContract: Accessor<BillingStatusContract | undefined>;
  currentPlan: Accessor<Subscription["plan"] | null | undefined>;
  companyId: Accessor<string | undefined>;
};

const BillingContext = createContext<BillingContextValue>();

export const BillingProvider: ParentComponent = (props) => {
  const subscriptionQuery = useSubscription();
  const billingStatusQuery = useBillingStatus();
  const [store] = useCompanyStore();

  // Le quote esaurite non si ascoltano più da qui: `PlanLimitWatcher` (montato
  // dentro il router, sopra ogni rotta) è l'unica superficie che risponde a un
  // `403 plan_limit_exceeded`. Stare fuori dal router costava una navigazione
  // via `window.location.href`, e stare dietro `isBillingEnabled()` costava il
  // silenzio in produzione, dove il flag è spento ma il backend rifiuta lo stesso.
  const contextValue: BillingContextValue = {
    subscription: () => subscriptionQuery.data?.subscription,
    billingStatusContract: () => billingStatusQuery.data,
    currentPlan: () => subscriptionQuery.data?.subscription?.plan,
    companyId: () => store.selectedCompany?.id,
  };

  return (
    <BillingContext.Provider value={contextValue}>
      {props.children}
    </BillingContext.Provider>
  );
};

export const useBillingContext = (): BillingContextValue => {
  const ctx = useContext(BillingContext);
  if (!ctx) {
    throw new Error("useBillingContext must be used within a BillingProvider");
  }
  return ctx;
};

export const useOptionalBillingContext = (): BillingContextValue | undefined =>
  useContext(BillingContext);
